import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {SizePipe} from "../../../../pipes/size.pipe";
import {CountPipe} from "../../../../pipes/count.pipe";
import customRulesFeatures from "./custom-rules-features.json";
import {Router} from "@angular/router";

interface Point {
  title: string;
  subPoints?: string[];
  soon?: boolean;
}

@Component({
    selector: 'db-platform-account-upgraded',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './platform-account-upgraded.component.html',
    styleUrls: ['./platform-account-upgraded.component.scss'],
    standalone: false
})
export class PlatformAccountUpgradedComponent implements OnInit {

  @Input() subscription: UserSubscription;
  @Input() upgradeableSubscription: UserSubscription | null;
  @Input() fromBooster?: boolean;
  @Input() isMobile?: boolean;
  @Output() onProceed: EventEmitter<void> = new EventEmitter<void>();

  public UserSubscriptionCategory = UserSubscriptionCategory;

  points: string[] = [];
  subPoints: string[] = [];
  secondPoints: string[] = [];
  secondSubPoints: string[] = [];
  customPoints: Point[] = [
    {
      title: 'Frontend Speed Optimization:'
    },
    {
      title: 'Backend Speed Optimization: with 10Web Automated Hosting'
    },
  ];
  public newPlans: boolean;

  constructor(
    private count: CountPipe,
    private size: SizePipe,
    private _router: Router,
  ) {
  }

  ngOnInit() {
    this.newPlans = !!this.subscription.newPlan;

    if (this.fromBooster) {
      this.getCustomRulesFeatures();
    } else {
      this.getUpgradedFeatures();
    }
  }


  private getCustomRulesFeatures(): void {
    this.customPoints[0].subPoints = customRulesFeatures['frontend_optimization'];
    this.customPoints[1].subPoints = customRulesFeatures['backend_optimization'];
  }


  private getUpgradedFeatures(): void {
    if (!this.subscription || !this.subscription.plan || !this.subscription.plan.services
      || !this.subscription.plan.services.hosting
      || !this.subscription.plan.services.hosting.hosting_visits
      || !this.subscription.plan.services.hosting.hosting_visits.parameter_value
      || !this.subscription.plan.services.hosting.hosting_storage
      || !this.subscription.plan.services.hosting.hosting_storage.parameter_value
      || !this.subscription.plan.services.Backup
      || !this.subscription.plan.services.Backup.storage
      || !this.subscription.plan.services.Backup.storage.parameter_value) {
      return;
    }

    if (this.subscription.apiResellerPro) {
      const regenerationWebsitesCount: number = parseInt(this.subscription?.plan?.services?.ai_builder.regeneration_count.parameter_value, 10);
      const regenerationAdditionalWebsitesCount: number = parseInt(
          this.subscription?.plan?.additionalDomainCount &&
          this.subscription?.plan?.services?.ai_builder.regeneration_additional_count ?
            this.subscription?.plan?.services?.ai_builder.regeneration_additional_count.parameter_value : 0, 10)
        * this.subscription?.plan?.additionalDomainCount;
      const totalWebsitesCount = regenerationWebsitesCount + regenerationAdditionalWebsitesCount;
      this.points = [
        `Full 10Web Website Builder API access`,
        `${ +this.subscription?.plan?.services?.hosting?.domain_count?.parameter_value + (this.subscription?.newPlan ? +this.subscription?.plan?.additionalDomainCount : +this.subscription?.additionalWebsiteCount)} websites (created & hosted via API)`,
        `${[totalWebsitesCount]} AI website generations`,
        `Enable the drag-and-drop editor for each site`,
        `Manage cloud hosting directly via API`,
        `${this.size.transform(this.subscription.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)} storage across all hosted sites`,
        `Up to ${this.count.transform(this.subscription.plan.services.hosting.hosting_visits.parameter_value)} monthly visitors across all hosted sites`,
        `Priority support via dedicated Slack channel`
      ];
    }
    else {
      if (this.subscription.isBusiness && this.subscription.category !== UserSubscriptionCategory.PROFESSIONAl) {
        this.points = [
          '10Web AI builder',
          'Premium Google cloud hosting',
          `${this.count.transform(this.subscription.plan.services.hosting.hosting_visits.parameter_value)} monthly visitors`,
          `${this.size.transform(this.subscription.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)} SSD storage`,
          'Free SSL certificate'
        ];
        if ([UserSubscriptionCategory.PERSONAL, UserSubscriptionCategory.PREMIUM].includes(this.subscription.category)) {
          this.points.push('Website speed booster');
          this.points.push('24/7 Live chat support');
        } else if (this.subscription.category === UserSubscriptionCategory.ENTERPRISE) {
          this.points.push('Cloudflare Enterprise CDN');
          this.points.push('Website speed booster');
          this.points.push('VIP priority support');
        } else if (this.subscription.category === UserSubscriptionCategory.ENTERPRISE_ULTIMATE) {
          this.points = [
            'Everything in AI Ultimate plan',
            '5 hours monthly development by a 10Web expert',
            'Website changes',
          ];
          this.subPoints = [
            'Website design updates',
            'Layout edits & revisions',
            'Page elements edits',
            '3rd-party integrations',
            'Menu changes & updates'
          ];

          this.secondPoints = [
            'Maintenance'
          ];
          this.secondSubPoints = [
            'Website performance monitoring',
            'Advanced troubleshooting',
            'Plugin & theme updates',
            'Website speed optimization',
            'Uptime monitoring', ];
        }
      } else if (this.subscription.isEcommerce && this.subscription.category !== UserSubscriptionCategory.PROFESSIONAl) {
        this.points = [
          '10Web AI builder',
          'AI powered ecommerce features',
          'Premium Google cloud hosting',
          `${this.count.transform(this.subscription.plan.services.hosting.hosting_visits.parameter_value)} monthly visitors`,
          `${this.size.transform(this.subscription.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)} SSD storage`,
          'Free SSL certificate'
        ];
        if ([UserSubscriptionCategory.PERSONAL].includes(this.subscription.category)) {
          this.points.push('Website speed booster');
          this.points.push('24/7 Live chat support');
        } else if ([UserSubscriptionCategory.PREMIUM, UserSubscriptionCategory.ENTERPRISE].includes(this.subscription.category)) {
          this.points.push('Cloudflare Enterprise CDN');
          this.points.push('Website speed booster');
          this.points.push('24/7 Live chat support');
        }
      } else if (this.subscription.isAgency) {
        this.points = [
          `${parseInt(this.subscription.plan.services.hosting.domain_count.parameter_value, 10)} hosted websites`,
          '10Web AI builder',
          'Premium Google cloud hosting',
          `${this.count.transform(this.subscription.plan.services.hosting.hosting_visits.parameter_value)} monthly visitors`,
          `${this.size.transform(this.subscription.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)} SSD storage`,
          '1 website with Cloudflare CDN',
          'Website speed booster',
          'Agency features'
        ];
      } else if (this.subscription.category === UserSubscriptionCategory.PROFESSIONAl && this.subscription.hasProfessionalWebsite) {
        this.points = [
          `All features of ${this.subscription.title}  plan`,
          'Informative website development',
        ];
        this.subPoints = [
          'Responsive design for all screen sizes',
          'Premium widgets for advanced functionality',
          'Blog functionality',
          '90+ PageSpeed score to boost SEO',
          'Free SSL certificate',
          'Up to 8 pages',
          '3-rd party integrations',
        ];
      } else if (this.subscription.category === UserSubscriptionCategory.PROFESSIONAl && this.subscription.hasEcommerceWebsite) {
        this.points = [
          `All features of ${this.subscription.title} plan`,
          'Ecommerce website development',
        ];
        this.subPoints = [
          'Responsive design for all screen sizes',
          'Premium widgets for advanced functionality',
          'Blog functionality',
          '90+ PageSpeed score to boost SEO',
          'Free SSL certificate',
          'Up to 10 pages',
          '3-rd party integrations',
          'Advanced Ecommerce functionality',
          'Intuitive dashboard to monitor and manage your ecommerce store',
          'Create new product pages with AI',
          'Accept credit card payments',
        ];
      }
    }
  }

  public proceed(): void {
    this.onProceed.emit();
    this.subscription?.apiResellerPro && !this.subscription?.isApiResellerPlan && !this.isMobile && this._router.navigate(['10web-api']);
  }
}
